'use es6';

import PropTypes from 'prop-types';
import { byId as TemplateTypes } from 'ContentUtils/constants/TemplateTypes';
import { FINDER_BULK_DELETE, FINDER_BULK_MOVE, FINDER_BULK_PORTAL_COPY, FINDER_FILE_CREATE, FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_RENAME, FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_SHOW_REVISIONS, FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_CREATE, FINDER_FOLDER_DELETE, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_MOVE_FILE, FINDER_FILE_SHOW_INFORMATION, FINDER_FOLDER_MOVE_FOLDER, FINDER_FOLDER_RENAME, FINDER_ASSETS_PORTAL_COPY, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_COPY_PUBLIC_URL, FINDER_FILE_COPY_MODULE_SNIPPET, FINDER_FILE_VIEW_LAYOUT_SOURCE, FINDER_FILE_CREATE_PAGE, FINDER_FILE_CREATE_EMAIL } from 'DesignData/actions/ActionTypes';
import * as DesignAssetTypes from 'DesignData/enums/DesignAssetTypes';
import * as RawAssetFileTypes from 'DesignData/enums/RawAssetFileTypes';
import * as FolderAssetTypes from 'DesignData/constants/FolderAssetTypes';
import * as FolderSubcategories from 'DesignData/enums/FolderSubcategories';
import FileModel from 'DesignData/models/finder/File';
import FolderModel from 'DesignData/models/finder/Folder';
import * as CosIndexConnectionCategories from 'DesignData/constants/CosIndexConnectionCategories';
const TEMPLATE_TYPE_IDS = Object.values(TemplateTypes || {}).map(templateType => templateType.id);
export const assetId = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);
export const bulkPermissions = PropTypes.shape({
  [FINDER_BULK_DELETE]: PropTypes.bool,
  [FINDER_BULK_MOVE]: PropTypes.bool,
  [FINDER_BULK_PORTAL_COPY]: PropTypes.bool
});
export const selectedAssets = PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(FileModel), PropTypes.instanceOf(FolderModel)]));
export const dependentsByCategory = PropTypes.shape(Object.keys(CosIndexConnectionCategories).reduce((hash, category) => {
  hash[category] = PropTypes.object;
  return hash;
}, {}));
export const FilePropTypes = Object.freeze({
  file: PropTypes.instanceOf(FileModel),
  fileId: assetId,
  fileName: PropTypes.node,
  filePermissions: PropTypes.shape({
    [FINDER_FILE_DELETE]: PropTypes.bool,
    [FINDER_FILE_DUPLICATE]: PropTypes.bool,
    [FINDER_FILE_RENAME]: PropTypes.bool,
    [FINDER_FILE_SHOW_DEPENDENTS]: PropTypes.bool,
    [FINDER_FILE_SHOW_REVISIONS]: PropTypes.bool,
    [FINDER_ASSETS_PORTAL_COPY]: PropTypes.bool
  }),
  fileTemplateType: PropTypes.oneOf(TEMPLATE_TYPE_IDS),
  fileType: PropTypes.oneOf(Object.values(FolderAssetTypes || {}).concat(Object.values(RawAssetFileTypes || {}))),
  fileAssetType: PropTypes.oneOf(Object.values(DesignAssetTypes || {})),
  folderPath: PropTypes.string,
  isRenamingFile: PropTypes.bool
});
export const FolderPropTypes = Object.freeze({
  folder: PropTypes.instanceOf(FolderModel),
  folderContents: PropTypes.shape({
    files: PropTypes.arrayOf(assetId),
    order: PropTypes.arrayOf(assetId),
    subfolders: PropTypes.arrayOf(assetId)
  }),
  folderId: assetId,
  folderName: PropTypes.string,
  folderPermissions: PropTypes.shape({
    [FINDER_FILE_CREATE]: PropTypes.bool,
    [FINDER_FOLDER_CREATE]: PropTypes.bool,
    [FINDER_FOLDER_DELETE]: PropTypes.bool,
    [FINDER_FOLDER_DUPLICATE]: PropTypes.bool,
    [FINDER_FOLDER_MOVE_FILE]: PropTypes.bool,
    [FINDER_FOLDER_MOVE_FOLDER]: PropTypes.bool,
    [FINDER_FOLDER_RENAME]: PropTypes.bool,
    [FINDER_ASSETS_PORTAL_COPY]: PropTypes.bool
  }),
  folderSubcategory: PropTypes.oneOf(Object.values(FolderSubcategories || {})),
  isRenamingFolder: PropTypes.bool,
  isUserFolder: PropTypes.bool
});
export const AssetActionsPropTypes = Object.freeze({
  assetActions: PropTypes.shape({
    primaryActions: PropTypes.arrayOf(PropTypes.string),
    secondaryActions: PropTypes.arrayOf(PropTypes.string),
    deleteAction: PropTypes.arrayOf(PropTypes.string)
  }),
  actionHandlers: PropTypes.shape({
    [FINDER_FILE_DUPLICATE]: PropTypes.func,
    [FINDER_FILE_RENAME]: PropTypes.func,
    [FINDER_FOLDER_COPY_PATH]: PropTypes.func,
    [FINDER_FOLDER_DUPLICATE]: PropTypes.func,
    [FINDER_FOLDER_RENAME]: PropTypes.func,
    [FINDER_FILE_SHOW_DEPENDENTS]: PropTypes.func,
    [FINDER_FILE_SHOW_REVISIONS]: PropTypes.func,
    [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: PropTypes.func,
    [FINDER_FILE_COPY_PUBLIC_URL]: PropTypes.func,
    [FINDER_FILE_COPY_MODULE_SNIPPET]: PropTypes.func,
    [FINDER_FILE_VIEW_LAYOUT_SOURCE]: PropTypes.func,
    [FINDER_FILE_SHOW_INFORMATION]: PropTypes.func,
    [FINDER_FOLDER_MOVE_FILE]: PropTypes.func,
    [FINDER_FILE_DELETE]: PropTypes.func,
    [FINDER_BULK_MOVE]: PropTypes.func,
    [FINDER_BULK_DELETE]: PropTypes.func,
    [FINDER_FOLDER_DELETE]: PropTypes.func,
    [FINDER_FILE_CREATE_PAGE]: PropTypes.func,
    [FINDER_FILE_CREATE_EMAIL]: PropTypes.func,
    [FINDER_ASSETS_PORTAL_COPY]: PropTypes.func,
    [FINDER_BULK_PORTAL_COPY]: PropTypes.func
  }),
  assetPermissions: PropTypes.shape({
    [FINDER_FILE_DUPLICATE]: PropTypes.bool,
    [FINDER_FILE_RENAME]: PropTypes.bool,
    [FINDER_FOLDER_COPY_PATH]: PropTypes.bool,
    [FINDER_FOLDER_DUPLICATE]: PropTypes.bool,
    [FINDER_FOLDER_RENAME]: PropTypes.bool,
    [FINDER_FILE_SHOW_DEPENDENTS]: PropTypes.bool,
    [FINDER_FILE_SHOW_REVISIONS]: PropTypes.bool,
    [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: PropTypes.bool,
    [FINDER_FILE_COPY_PUBLIC_URL]: PropTypes.bool,
    [FINDER_FILE_COPY_MODULE_SNIPPET]: PropTypes.bool,
    [FINDER_FILE_VIEW_LAYOUT_SOURCE]: PropTypes.bool,
    [FINDER_FOLDER_MOVE_FILE]: PropTypes.bool,
    [FINDER_FILE_SHOW_INFORMATION]: PropTypes.bool,
    [FINDER_FILE_DELETE]: PropTypes.bool,
    [FINDER_BULK_MOVE]: PropTypes.bool,
    [FINDER_BULK_DELETE]: PropTypes.bool,
    [FINDER_FOLDER_DELETE]: PropTypes.bool,
    [FINDER_FILE_CREATE_PAGE]: PropTypes.bool,
    [FINDER_FILE_CREATE_EMAIL]: PropTypes.bool,
    [FINDER_ASSETS_PORTAL_COPY]: PropTypes.bool,
    [FINDER_BULK_PORTAL_COPY]: PropTypes.bool
  })
});